/** The node model (SPEC §5.1). Everything is a node; records are immutable and replaced on change. */
import type { NodeId } from './ids.ts'

export type NodeKind = 'block' | 'page' | 'journal' | 'tag' | 'asset' | 'view'
export type Origin = 'user' | 'ai' | 'import' | 'calendar'

export interface Node {
  readonly id: NodeId
  readonly kind: NodeKind
  /** Markdown dialect (SPEC §6.1). */
  readonly content: string
  /** Pages, tags, people, companies; unique per graph (case-insensitive). Blocks have none. */
  readonly title: string | null
  /** null only for top-level kinds (page, journal, tag, view, asset without owner). */
  readonly parentId: NodeId | null
  /** Fractional index key among siblings. */
  readonly order: string
  readonly tags: readonly NodeId[]
  readonly props: Readonly<Record<string, unknown>>
  /** Journal day key, YYYY-MM-DD in the device's local calendar (SPEC D21). */
  readonly date: string | null
  readonly collapsed: boolean
  readonly private: boolean
  readonly origin: Origin
  readonly createdAt: number
  readonly updatedAt: number
  /** Soft delete. Deleting a node cascades to its live descendants, so live ⇒ parent live. */
  readonly deletedAt: number | null
}

export const MAX_CONTENT = 20_000
export const MAX_TITLE = 500

export function makeNode(fields: Partial<Node> & Pick<Node, 'id' | 'kind'>): Node {
  return {
    content: '',
    title: null,
    parentId: null,
    order: 'a0',
    tags: [],
    props: {},
    date: null,
    collapsed: false,
    private: false,
    origin: 'user',
    createdAt: 0,
    updatedAt: 0,
    deletedAt: null,
    ...fields,
  }
}
